(function () {
  "use strict";

  var PROXY_ROOT = "/apps/naxarly-booking";
  var IDEMPOTENCY_PREFIX = "naxarly-hold-key:";
  var CART_HOLD_STORAGE_KEY = "naxarly-cart-hold-reference";

  function apiError(response, body, fallback) {
    var error = new Error((body && body.message) || fallback);
    error.code = (body && body.code) || "request_failed";
    error.status = response.status;
    return error;
  }

  async function post(path, body) {
    var response = await fetch(PROXY_ROOT + path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(body)
    });
    var result = await response.json().catch(function () { return {}; });
    if (!response.ok) throw apiError(response, result, "Booking could not be completed.");
    return result;
  }

  function eventStartsAt(availability) {
    if (!availability.date || !availability.time) {
      throw new Error("Please select your event date and start time.");
    }
    var value = availability.date + "T" + availability.time + ":00+05:30";
    var date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      throw new Error("Please select a valid event date and start time.");
    }
    return date.toISOString();
  }

  function destination(availability) {
    var latitude = Number(availability.latitude);
    var longitude = Number(availability.longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      throw new Error("Please select your event location again.");
    }
    return {
      label: availability.label || availability.city,
      city: availability.city,
      latitude: latitude,
      longitude: longitude
    };
  }

  function selectedAddons(state, packageStableKey) {
    return state.items
      .filter(function (item) {
        return item.type === "addon" &&
          item.parentPackageStableKey === packageStableKey &&
          item.stableKey &&
          Number(item.quantity) > 0;
      })
      .map(function (item) {
        return {
          stableKey: item.stableKey,
          quantity: Math.max(1, Number(item.quantity) || 1)
        };
      });
  }

  function selections(state) {
    var services = state.items.filter(function (item) {
      return item.type !== "addon" && item.stableKey;
    });
    if (!services.length) throw new Error("Add at least one service to your event.");
    return services.map(function (service) {
      return {
        packageStableKey: service.stableKey,
        addonStableKeys: [],
        addons: selectedAddons(state, service.stableKey)
      };
    });
  }

  function fingerprint(value) {
    var source = JSON.stringify(value);
    var hash = 2166136261;
    for (var index = 0; index < source.length; index += 1) {
      hash ^= source.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16);
  }

  function idempotencyKey(payload) {
    var key = IDEMPOTENCY_PREFIX + fingerprint(payload);
    var existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    var created = window.crypto && typeof window.crypto.randomUUID === "function"
      ? window.crypto.randomUUID()
      : Date.now().toString(36) + "-" + Math.random().toString(36).slice(2);
    window.sessionStorage.setItem(key, created);
    return created;
  }

  function availabilityPayload(slot) {
    var selection = window.Naxarly &&
      window.Naxarly.ServicePage &&
      window.Naxarly.ServicePage.getSelection();
    if (!selection || !selection.stableKey) {
      throw new Error("Please select a package first.");
    }
    var state = window.Naxarly.Event.getState();
    var saved = Object.assign({}, state.availability, {
      date: slot.date,
      time: slot.time,
      city: slot.location
    });
    var addons = selectedAddons(state, selection.stableKey);
    return {
      packageStableKey: selection.stableKey,
      addonStableKeys: [],
      addons: addons,
      startsAt: eventStartsAt(saved),
      destination: destination(saved)
    };
  }

  async function checkAvailability(slot) {
    var result = await post("/availability", availabilityPayload(slot));
    return {
      status: result.available ? "available" : "unavailable",
      available: result.available,
      code: result.code,
      message: result.message,
      eventStartsAt: result.eventStartsAt,
      eventEndsAt: result.eventEndsAt,
      travelFeeMode: result.travelFeeMode
    };
  }

  async function getCart() {
    var response = await fetch((window.Shopify?.routes?.root || "/") + "cart.js", {
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error("Unable to check the Shopify cart.");
    return response.json();
  }

  function cartContainsHold(cart, reference) {
    return cart.items.some(function (item) {
      return item.properties && item.properties["_Naxarly hold"] === reference;
    });
  }

  async function removeOtherNaxarlyCartLines(cart, reference) {
    var updates = {};
    var replacedHolds = [];
    cart.items.forEach(function (item) {
      var holdReference = item.properties && item.properties["_Naxarly hold"];
      if (holdReference && holdReference !== reference) {
        updates[item.key] = 0;
        if (replacedHolds.indexOf(holdReference) === -1) {
          replacedHolds.push(holdReference);
        }
      }
    });
    if (!Object.keys(updates).length) return;

    var root = window.Shopify?.routes?.root || "/";
    var response = await fetch(root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ updates: updates })
    });
    if (!response.ok) {
      throw new Error("Unable to replace the previous event in your cart.");
    }
    await Promise.all(replacedHolds.map(releaseHold));
    forgetCartHold();
  }

  function rememberCartHold(reference) {
    if (!reference) return;
    window.localStorage.setItem(CART_HOLD_STORAGE_KEY, reference);
  }

  function forgetCartHold() {
    window.localStorage.removeItem(CART_HOLD_STORAGE_KEY);
    for (var index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      var key = window.sessionStorage.key(index);
      if (key && key.indexOf(IDEMPOTENCY_PREFIX) === 0) {
        window.sessionStorage.removeItem(key);
      }
    }
  }

  async function releaseHold(reference) {
    if (!reference) return;
    var response = await fetch(
      PROXY_ROOT + "/holds/" + encodeURIComponent(reference),
      {
        method: "DELETE",
        headers: { Accept: "application/json" }
      }
    );
    if (!response.ok) {
      throw new Error("Unable to release the previous event selection.");
    }
  }

  async function reconcileEventCart() {
    var reference = window.localStorage.getItem(CART_HOLD_STORAGE_KEY);
    if (!reference) return { reconciled: false, cartContainsHold: false };

    var cart = await getCart();
    var containsHold = cartContainsHold(cart, reference);
    if (containsHold) {
      return { reconciled: true, cartContainsHold: true };
    }

    await releaseHold(reference);
    forgetCartHold();
    if (window.Naxarly && window.Naxarly.Event) {
      window.Naxarly.Event.clearServices();
    }
    return { reconciled: true, cartContainsHold: false };
  }

  function cartLines(state, hold) {
    return state.items
      .filter(function (item) {
        var selected = item.type === "addon"
          ? hold.addonStableKeys.includes(item.stableKey)
          : hold.packageStableKeys.includes(item.stableKey);
        return selected && item.variantId && Number(item.quantity) > 0;
      })
      .map(function (item) {
        return {
          id: Number(item.variantId),
          quantity: Math.max(1, Number(item.quantity) || 1),
          properties: {
            "_Naxarly hold": hold.reference,
            "_Naxarly item": item.stableKey || "",
            "_Naxarly package": item.type === "addon"
              ? item.parentPackageStableKey || ""
              : item.stableKey || "",
            "Event date": state.availability.date,
            "Event start time": state.availability.time,
            "Event location": state.availability.label || state.availability.city
          }
        };
      });
  }

  async function addHoldToCart(state, hold) {
    var cart = await getCart();
    await removeOtherNaxarlyCartLines(cart, hold.reference);
    if (cartContainsHold(cart, hold.reference)) return;
    var root = window.Shopify?.routes?.root || "/";
    var response = await fetch(root + "cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({ items: cartLines(state, hold) })
    });
    var result = await response.json().catch(function () { return {}; });
    if (!response.ok) {
      throw apiError(response, result, "Unable to add the event to your cart.");
    }
    await fetch(root + "cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify({
        attributes: {
          "Naxarly hold reference__": hold.reference,
          "Naxarly hold expires__": hold.expiresAt
        }
      })
    });
  }

  async function checkout(state) {
    var availability = state.availability || {};
    var payload = {
      selections: selections(state),
      startsAt: eventStartsAt(availability),
      destination: destination(availability)
    };
    payload.idempotencyKey = idempotencyKey(payload);
    var hold = await post("/holds", payload);
    await addHoldToCart(state, hold);
    rememberCartHold(hold.reference);
    return hold;
  }

  window.Naxarly = window.Naxarly || {};
  window.Naxarly.Availability = { check: checkAvailability };
  window.Naxarly.Booking = {
    checkout: checkout,
    selections: selections,
    eventStartsAt: eventStartsAt,
    reconcileEventCart: reconcileEventCart
  };

  function reconcileSavedCheckout() {
    reconcileEventCart().catch(function () {
      /* Preserve the event basket if Shopify's cart is temporarily unavailable. */
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", reconcileSavedCheckout);
  } else {
    reconcileSavedCheckout();
  }
  window.addEventListener("pageshow", reconcileSavedCheckout);
})();
